import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useParams } from "react-router-dom";
import { Archive, Award, BarChart3, Calendar, ClipboardList, CreditCard, FileText, Grid3X3, Image, LayoutGrid, LogOut, Menu, Phone, Settings, Shield, ShoppingBag, Ticket, Trophy, UserCog, Users, X, ShieldCheck, Sparkles, Newspaper, Palette, Activity, ShieldAlert, Wallet, Inbox } from "lucide-react";
import { Logo } from "@/components/fsl/Logo";
import { NotificationsBell } from "@/components/fsl/NotificationsBell";
import { api } from "@/lib/api";
import { TournamentSwitcher } from "@/components/fsl/TournamentSwitcher";
import { OfflineBanner } from "@/components/fsl/States";
import { InstallBanner } from "@/components/fsl/InstallBanner";
import { useAuth } from "@/context/AuthContext";
import { useTournaments } from "@/context/TournamentContext";
import { ROLE_LABELS } from "@/lib/format";

const HUB_NAV = [
  { to: "/admin", label: "Tornei", icon: Trophy, end: true },
  { to: "/admin/societa", label: "Tutte le società", icon: Shield, roles: ["super_admin", "director", "secretary"] },
  { to: "/admin/utenti", label: "Utenti", icon: UserCog, roles: ["super_admin", "director", "secretary"] },
  { to: "/admin/traffico", label: "Traffico", icon: Activity, superOnly: true },
  { to: "/admin/sicurezza", label: "Sicurezza", icon: ShieldAlert, superOnly: true },
  { to: "/admin/contabilita", label: "Contabilità FSL", icon: Wallet, financeOnly: true },
];

const TOURNAMENT_NAV = (id) => [
  { to: `/admin/t/${id}`, label: "Overview", icon: LayoutGrid, end: true },
  { to: `/admin/t/${id}/impostazioni`, label: "Impostazioni", icon: Settings },
  { to: `/admin/t/${id}/competizioni`, label: "Competizioni", icon: Trophy },
  { to: `/admin/t/${id}/societa`, label: "Società", icon: Shield },
  { to: `/admin/t/${id}/campi`, label: "Campi", icon: Grid3X3 },
  { to: `/admin/t/${id}/calendario`, label: "Gironi e Calendario", icon: Calendar },
  { to: `/admin/t/${id}/partite`, label: "Partite", icon: ClipboardList },
  { to: `/admin/t/${id}/referti`, label: "Referti", icon: FileText },
  { to: `/admin/t/${id}/richieste`, label: "Richieste", icon: Inbox },
  { to: `/admin/t/${id}/classifiche`, label: "Classifiche", icon: BarChart3 },
  { to: `/admin/t/${id}/rose`, label: "Rose", icon: Users },
  { to: `/admin/t/${id}/premi`, label: "Premi", icon: Award },
  { to: `/admin/t/${id}/weekly`, label: "FSL Weekly · Top 11", icon: Newspaper, match: ["weekly", "top11"] },
  { to: `/admin/t/${id}/studio`, label: "Social Studio", icon: Palette },
  { to: `/admin/t/${id}/legacy`, label: "Albo d'oro", icon: Archive },
  { to: `/admin/t/${id}/pagamenti`, label: "Pagamenti e vendite", icon: CreditCard, match: ["pagamenti", "vendite", "negozio", "ospitalita", "monetizzazione"] },
  { to: `/admin/t/${id}/comunicazioni`, label: "Contatti", icon: Phone },
  { to: `/admin/t/${id}/ticket`, label: "Ticket", icon: Ticket },
  { to: `/admin/t/${id}/media`, label: "Blog e interviste", icon: Image },
  { to: `/admin/t/${id}/documenti`, label: "Documenti", icon: FileText },
  { to: `/admin/t/${id}/audit`, label: "Audit", icon: Archive },
];

function NavItem({ to, label, icon: Icon, end, onClick, match, badge }) {
  const { pathname } = useLocation();
  const forced = match?.some((m) => pathname.split("/").includes(m));
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={`sidebar-nav-${label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 min-h-[44px] py-2 pl-4 pr-3 text-[12.5px] leading-tight font-semibold uppercase tracking-wide border-l-[3px] transition-colors ${
          isActive || forced ? "border-fsl-gold text-fsl-gold bg-white/[0.06]" : "border-transparent text-fsl-slate hover:text-fsl-white hover:bg-white/[0.04]"
        }`
      }
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="break-words">{label}</span>
      {badge > 0 && <span className="ml-auto h-5 min-w-[20px] px-1.5 rounded-full bg-fsl-gold text-ink-950 text-[10px] font-bold num inline-flex items-center justify-center" data-testid={`sidebar-badge-${label.toLowerCase()}`}>{badge}</span>}
    </NavLink>
  );
}

export default function AdminShell() {
  const { user, logout } = useAuth();
  const { current, currentId, setCurrentId } = useTournaments();
  const { tournamentId } = useParams();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (tournamentId) setCurrentId(tournamentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  useEffect(() => setOpen(false), [location.pathname]);

  const [pendingAccess, setPendingAccess] = useState(0);
  const [pendingRose, setPendingRose] = useState(0);
  const [pendingOrg, setPendingOrg] = useState(0);
  const [pendingRequests, setPendingRequests] = useState(0);
  const scopeId = tournamentId || currentId;
  useEffect(() => {
    const load = () => {
      api.get("/access-requests/pending-count").then((r) => setPendingAccess(r.data.count)).catch(() => {});
      api.get("/org-groups/pending-count").then((r) => setPendingOrg(r.data.count)).catch(() => {});
      if (scopeId) api.get(`/tournaments/${scopeId}/club-groups/pending-count`).then((r) => setPendingRose(r.data.count)).catch(() => setPendingRose(0));
      if (scopeId) api.get(`/tournaments/${scopeId}/requests-board/count`).then((r) => setPendingRequests(r.data.total)).catch(() => setPendingRequests(0));
    };
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [location.pathname, scopeId]);

  const [finance, setFinance] = useState(false);
  useEffect(() => { if (user.is_super_admin) api.get("/finance/access").then((r) => setFinance(!!r.data.allowed)).catch(() => setFinance(false)); }, [user.is_super_admin]);
  const hubNav = HUB_NAV.filter((n) => (n.financeOnly ? finance : n.superOnly ? user.is_super_admin : !n.roles || user.is_super_admin || n.roles.includes(user.role)));
  const leaveScope = () => setCurrentId(null);

  const sidebar = (
    <aside className="flex h-full w-[220px] flex-col bg-ink-950 border-r border-white/10" data-testid="admin-sidebar">
      <div className="px-4 h-[76px] flex items-center border-b border-white/10">
        <Logo to="/admin" compact />
        <span className="ml-2 font-display font-extrabold uppercase text-sm leading-tight">
          Future
          <br />
          Stars
        </span>
      </div>
      <nav className="flex-1 overflow-y-auto py-3 fsl-scroll" aria-label="Navigazione amministrativa">
        <div className="px-4 pb-1 text-[10px] uppercase tracking-widest text-fsl-slate/70">Hub</div>
        {hubNav.map((n) => (n.end && scopeId ? <NavItem key={n.to} to={n.to} label="Tutti i tornei" icon={LayoutGrid} end onClick={leaveScope} /> : <NavItem key={n.to} {...n} badge={n.to === "/admin/utenti" ? pendingAccess : n.to === "/admin/societa" ? pendingOrg : 0} />))}
        {scopeId && (
          <>
            <div className="px-4 pt-4 pb-1 text-[10px] uppercase tracking-widest text-fsl-slate/70 truncate" title={current?.name}>
              {current?.name || "Torneo"}
            </div>
            {TOURNAMENT_NAV(scopeId).map((n) => (
              <NavItem key={n.to} {...n} badge={n.label === "Rose" ? pendingRose : n.label === "Richieste" ? pendingRequests : 0} />
            ))}
          </>
        )}
      </nav>
      <div className="border-t border-white/10 p-4">
        <div className="text-xs text-fsl-slate">{ROLE_LABELS[user.role]}</div>
        <div className="text-sm font-semibold truncate" data-testid="sidebar-user-name">
          {user.full_name}
        </div>
        <Link to="/sicurezza" className="mt-3 flex items-center gap-2 text-xs text-fsl-slate hover:text-fsl-white transition-colors" data-testid="security-link"><ShieldCheck className="h-3.5 w-3.5" /> Sicurezza account</Link>
        <button onClick={logout} className="mt-2 flex items-center gap-2 text-xs text-fsl-slate hover:text-fsl-white transition-colors" data-testid="logout-button">
          <LogOut className="h-4 w-4" aria-hidden="true" /> Log out
        </button>
      </div>
    </aside>
  );

  return (
    <div className="min-h-screen flex bg-navy-900">
      <div className="hidden xl:block sticky top-0 h-screen">{sidebar}</div>
      {open && (
        <div className="fixed inset-0 z-50 xl:hidden">
          <button className="absolute inset-0 bg-ink-950/70" aria-label="Chiudi menu" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0">{sidebar}</div>
          <button className="absolute left-[176px] top-4 h-11 w-11 rounded-md bg-navy-800 border border-white/20 flex items-center justify-center" onClick={() => setOpen(false)} aria-label="Chiudi">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <OfflineBanner />
        <InstallBanner />
        <header className="sticky top-0 z-40 h-[76px] flex items-center justify-between gap-4 px-4 md:px-6 bg-navy-900/85 backdrop-blur-md border-b border-white/10">
          <div className="flex items-center gap-3 min-w-0">
            <button className="xl:hidden h-11 w-11 rounded-md border border-white/20 flex items-center justify-center" onClick={() => setOpen(true)} aria-label="Apri menu" data-testid="sidebar-toggle">
              <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <div className="fsl-kicker">Control Room</div>
              <div className="text-sm text-fsl-slate truncate">
                {user.full_name} · {ROLE_LABELS[user.role]}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <TournamentSwitcher />
            <NotificationsBell staff />
          </div>
        </header>
        <main className="flex-1 px-4 md:px-6 py-6 max-w-[1488px] w-full mx-auto gold-skin">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
